import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { usePostHog } from 'posthog-js/react'
import { api, OnboardingStatus } from '../../api/client'
import './SetupChecklist.css'

interface SetupChecklistProps {
    status: OnboardingStatus
    onDismiss: () => void
}

interface ChecklistItem {
    key: keyof OnboardingStatus['checklist']
    label: string
    linkTo: string
    /** True if this item only shows for Family coverage */
    familyOnly?: boolean
    /** True if this item is gated to Core/Pro */
    corePlus?: boolean
}

const ITEMS: ChecklistItem[] = [
    { key: 'hasHsaAccount', label: 'Add your HSA account', linkTo: '/accounts' },
    { key: 'hasFamilyMember', label: 'Add family members', linkTo: '/accounts', familyOnly: true },
    { key: 'hasImportedExpenses', label: 'Import your expenses', linkTo: '/expenses/import' },
    { key: 'hasLinkedDocument', label: 'Upload a receipt', linkTo: '/expenses' },
    { key: 'hasEmailInbox', label: 'Set up your email inbox', linkTo: '/settings', corePlus: true },
    { key: 'hasViewedDashboard', label: 'Explore your reimbursable balance', linkTo: '/dashboard' },
]

export default function SetupChecklist({ status, onDismiss }: SetupChecklistProps) {
    const posthog = usePostHog()
    const [isDismissing, setIsDismissing] = useState(false)
    const prevCompletedCount = useRef(status.completedCount)

    useEffect(() => {
        if (status.completedCount > prevCompletedCount.current) {
            posthog.capture('checklist_item_completed', {
                completedCount: status.completedCount,
                totalCount: status.totalCount
            })
        }
        prevCompletedCount.current = status.completedCount
    }, [status.completedCount, status.totalCount, posthog])

    const isFamily = status.coverageType === 'Family'
    const isCorePlus = status.userTier === 'Core' || status.userTier === 'Pro'
    const isFree = !isCorePlus

    // Filter and update items based on path, coverage type, and tier visibility
    const visibleItems = ITEMS.map((item) => {
        if (item.key === 'hasImportedExpenses') {
            if (status.pathChosen === 'AddExpense' || status.pathChosen === 'Explore') {
                return { ...item, label: 'Add your first expense', linkTo: '/expenses' }
            }
            return item
        }
        return item
    }).filter((item) => {
        if (item.familyOnly && !isFamily) return false
        if (item.corePlus && !isCorePlus) return false
        return true
    })

    // Don't render if dismissed or all items complete
    if (status.onboardingDismissed || status.completedCount >= status.totalCount) {
        return null
    }

    const handleDismiss = async () => {
        setIsDismissing(true)
        try {
            await api.dismissOnboarding()
            posthog.capture('checklist_dismissed', { completedCount: status.completedCount })
            onDismiss()
        } catch {
            setIsDismissing(false)
        }
    }

    return (
        <div className="setup-checklist" role="complementary" aria-label="Getting started checklist">
            <div className="checklist-header">
                <div>
                    <h2 className="checklist-title">Getting Started</h2>
                    <p className="checklist-progress">
                        {status.completedCount} of {status.totalCount} complete
                    </p>
                </div>
                <div className="checklist-progress-bar-wrap">
                    <div
                        className="checklist-progress-bar"
                        style={{ width: `${Math.round((status.completedCount / status.totalCount) * 100)}%` }}
                        role="progressbar"
                        aria-valuenow={status.completedCount}
                        aria-valuemin={0}
                        aria-valuemax={status.totalCount}
                    />
                </div>
            </div>

            <ul className="checklist-items" role="list">
                {visibleItems.map((item) => {
                    const isDone = status.checklist[item.key]
                    const isGated = item.corePlus && isFree

                    return (
                        <li
                            key={item.key}
                            className={`checklist-item${isDone ? ' checklist-item--done' : ''}${isGated ? ' checklist-item--gated' : ''}`}
                        >
                            <span className="checklist-icon" aria-hidden="true">
                                {isDone ? '✅' : isGated ? '🔒' : '○'}
                            </span>
                            {isGated ? (
                                <span className="checklist-label">
                                    {item.label}
                                    <span className="checklist-tier-badge" title="Requires Core plan">Core</span>
                                </span>
                            ) : (
                                <Link
                                    to={item.linkTo}
                                    className="checklist-label checklist-link"
                                    aria-label={isDone ? `${item.label} (completed)` : item.label}
                                >
                                    {item.label}
                                </Link>
                            )}
                        </li>
                    )
                })}
            </ul>

            <div className="checklist-footer">
                <button
                    className="checklist-dismiss"
                    onClick={handleDismiss}
                    disabled={isDismissing}
                    type="button"
                >
                    {isDismissing ? 'Dismissing…' : 'Dismiss'}
                </button>
                <span className="checklist-footnote">
                    You can re-access this from{' '}
                    <Link to="/settings" className="checklist-settings-link">Settings</Link>.
                </span>
            </div>
        </div>
    )
}
